import { inject } from "@angular/core";
import { Router } from "@angular/router";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { concatLatestFrom } from '@ngrx/operators';
import { Store, createSelector } from "@ngrx/store";
import { QueryOptionsAlone } from "apollo-angular/types";
import {
  DiscountsGQL,
  DiscountsQuery,
  DiscountsQueryVariables,
  GenerateChargesWithAiGQL,
  GenerateChargesWithAiQuery,
  GenerateChargesWithAiQueryVariables,
  PriceType,
} from "graphql.generated";
import { isNil } from "lodash";
import { DialogService } from "primeng/dynamicdialog";
import { catchError, concat, concatMap, debounceTime, distinctUntilChanged, exhaustMap, filter, groupBy, map, mergeMap, of, switchMap, take, tap } from "rxjs";

import { PermissionService } from "src/app/services/permission.service";
import { CancelAndCloseDialogActions } from "src/app/shared/cancel-and-close-dialog/cancel-and-close-dialog.actions";
import { UnsavedChangesBannerActions } from "src/app/shared/unsaved-changes-banner/unsaved-changes-banner.actions";
import { selectSaveableJobPageChanges, selectSharedCrewPayChanges } from "src/app/shared/unsaved-changes-banner/unsaved-changes-banner.selectors";
import { brandingFeature } from "src/app/state/branding.store";
import { selectUrl } from "src/app/state/router";
import { findPriceInProducts, getZoneActivePrice } from "src/app/utilities/product.util";
import { CreateCommentGQL, CrewPayAssignmentInput, SetCrewPayAssignmentsGQL, UpdateEventNavigatorGQL, UpdateJobGQL } from 'src/generated/graphql.generated';

import { authFeature } from '../../core/auth/auth.reducer';
import { generateMutationContext } from '../../core/auth/session';
import { HistoryService } from "../../history/history.service";

import { jobsFeature } from '../../jobs/jobs.reducer';
import { quickJsActions } from '../../quickjs/quickjs.actions';
import { selectCalculatedState, selectUserInputs } from '../../quickjs/quickjs.feature';
import { eventHelperUpdateEventSuccess } from "../../services/event-helper.service";
import { ShareEventZoneComponent } from "../../shared/share-event-zone/share-event-zone.component";
import { ActionSource } from "../../state/action-sources";
import { UnsavedChangesActions } from "../../state/unsaved-changes.actions";
import { unsavedChangesFeature } from "../../state/unsaved-changes.reducer";
import { generateUUID } from "../../utilities/state.util";
import { JobSummaryActions } from "../job-create/job-create-summary/job-create-summary.actions";
import { jobCreateSummaryFeature } from "../job-create/job-create-summary/job-create-summary.feature";
import { JobHeaderActions } from "../job-header/job-header.actions";
import { NavigatorOutboxActions } from "../job-navigator/navigator-outbox/navigator-outbox.actions";
import { sendOrQueueNavigatorWrite } from "../job-navigator/navigator-outbox/navigator-outbox.send";
import { mergeQueuedNavigatorData, opsRevisionToSend } from "../job-navigator/navigator-outbox/navigator-outbox.util";
import { OverviewTimelineActions } from "../job-overview/overview-timeline/overview-timeline.actions";
import { CreateEventDialogActions } from "../job-overview/overview-timeline/timeline-create-event/timeline-create-event.actions";
import { JobToolActions } from "../job-tool.actions";
import { selectJob, selectJobId, selectJobZone, selectNavigatorUrlForEvent } from "../job-tool.reducer";
import { LeaveConfirmationDialogActions } from "../leave-job-confirm/leave-job-confirm.actions";

import { WorkOrdersActions } from './job-workorders.actions';
import { jobWorkordersFeature } from './job-workorders.feature';
import { selectAddChargesDialogPresetCharges } from './job-workorders.selectors';
import { EditableCharge } from "./job-workorders.state";
import { crewPayAssignmentChangeKey, parseCrewPayAssignmentKey } from './mr-calculator/crew-pay-table/shared-crew-pay';
import { UpdateNavigatorDialogComponent } from "./update-navigator-dialog/update-navigator-dialog.component";
import { UpsellProductsDialogComponent } from "./upsell-products-dialog/upsell-products-dialog.component";

export const startEvent = createEffect(() => {
  const actions$ = inject(Actions);
  const store = inject(Store);
  const router = inject(Router);
  const updateEventNavigatorGQL = inject(UpdateEventNavigatorGQL);

  return actions$.pipe(
    ofType(WorkOrdersActions.startEventButtonClicked),
    concatLatestFrom(({ eventId }) => store.select(createSelector(
      selectJob,
      (job) => job?.events?.find((e) => e.id === eventId)
    ))),
    filter(([_action, event]) => Boolean(event)),
    concatLatestFrom(([_action, event]) => [
      store.select(selectJobId),
      store.select(selectNavigatorUrlForEvent(event)),
    ]),
    exhaustMap(([[{ eventId }, event], jobId, resolvedScriptUrl]) => of(null).pipe(
      switchMap(() => {
        if (!resolvedScriptUrl) {
          throw new Error(`No navigator URL configured for event type "${event.type}"`);
        }

        // Stamp the current step's start time so elapsed time survives a reload that
        // happens before the crew confirms anything.
        //
        // Deliberately does NOT seed an entry in `steps`. NavEventNavigatorComponent
        // already holds the active step's start time in `_stepStartTime` and persists it
        // on confirm, so seeding here is redundant — and it previously wrote a hardcoded
        // `confirm_customer_info`, a step that no longer exists in the script. That
        // phantom never completed, so it permanently satisfied the navigator's
        // "started but not completed" restore test and could hijack step restoration
        // once every real step had finished.
        const now = Date.now();
        let existingData: Record<string, any> = {};
        try { existingData = event.navigatorData ? JSON.parse(event.navigatorData) : {}; } catch { /* */ }

        // Only stamp on a fresh start. On resume ("Continue") the stored value is the
        // active step's real start time and must not be reset.
        const alreadyStarted = existingData?.stepStartTime != null;

        const patchObs = alreadyStarted
          ? of(null)
          : updateEventNavigatorGQL.mutate({
              eventId,
              currentStep: event.navigatorCurrentStep || 'started',
              // Only the key this write owns; the server merges it into the stored run (#48).
              navigatorData: JSON.stringify({ stepStartTime: now }),
            }, { context: generateMutationContext() });

        return patchObs.pipe(
          map(() => {
            router.navigate(['/jobs', jobId, 'navigator', eventId]);
            return WorkOrdersActions.promoteEventSuccess({ eventId, status: 'started' });
          }),
        );
      }),
      catchError((error) => {
        console.error(error);
        return of(WorkOrdersActions.promoteEventError({ error }));
      }),
    )),
  );
}, { functional: true, dispatch: true });

export const addCharges = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
) => {
    return actions$.pipe(
      ofType(WorkOrdersActions.addChargesButtonClicked),
      concatLatestFrom(() => store.select(createSelector(
        jobWorkordersFeature.selectAddChargesDialog,
        selectAddChargesDialogPresetCharges,
        ({ selectedEventId }, charges) => ({
          eventIds: [ selectedEventId ],
          charges,
        }),
      ))),
      map(([ _action, payload ]) => WorkOrdersActions.chargesAdded(payload)),
    );
  }, { functional:true, dispatch: true });


export const addCalculatorCharge = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
) => {
    return actions$.pipe(
      ofType(quickJsActions.addChargeRequested),
      exhaustMap((action) => of(action).pipe(
        concatLatestFrom(() => [
          store.select(brandingFeature.selectProducts),
          store.select(brandingFeature.selectBranding),
          store.select(selectJobZone),
        ]),
        map(([ action, availableProducts, { zoneId }, jobZone ]) => {
          const chargeInput = action.charge;

          const charge: EditableCharge = {
              chargeId: generateUUID(),
              quantity: 1,
              amount: 0,

              ...chargeInput,
              priceType: chargeInput.priceType as PriceType,
              expenses: undefined,
              taxes: chargeInput.taxes || [],

              new: true,
              modified: false,
              removed: false,
          };

          let product: (typeof availableProducts)[number];
          let price: (typeof product)['prices'][number];

          if (chargeInput.productId && !chargeInput.priceId) {
            product = availableProducts?.find((p) => p.id === chargeInput.productId);
            price = getZoneActivePrice(product?.prices, zoneId);
          } else if (chargeInput.productId && chargeInput.priceId) {
            product = availableProducts?.find((p) => p.id === chargeInput.productId);
            price = product?.prices?.find((p) => p.id === chargeInput.priceId);
          } else if (!chargeInput.productId && chargeInput.priceId) {
            const found = findPriceInProducts(availableProducts || [], chargeInput.priceId);
            product = found.product;
            price = found.price;
          }

          if (price && !charge.amount) {
            charge.amount = price.priceType === PriceType.Percentage ? price.amount : price.amount / 100;
            charge.priceType = price.priceType;
          }

          charge.productId = product?.id;
          charge.priceId = price?.id;

          if (!charge.taxes.length) {
            charge.taxes = (price?.taxes || [])
              .filter((t) => t.zones?.nodes?.some((z) => z?.id === zoneId || z?.id === jobZone?.id))
              .map((t) => ({ id: t.id, percentage: t.percentage }));
          }

          charge.expenses = charge.expenses || price?.expensesV2?.map((e) => ({
            // ...e.expense,
            expenseId: e.expense.id,
            unitCost: e.unitCost,
          })) || [];

          return {
            eventIds: [ action.eventId ],
            charges: [charge],
          };
        }),
        map((payload) => WorkOrdersActions.chargesAdded(payload)),
        catchError((error) => of(WorkOrdersActions.addCalculatorChargeError({ error }))),
      )),
    );
  }, { functional:true, dispatch: true });

export const duplicateEvent = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
) => {
    return actions$.pipe(
      ofType(WorkOrdersActions.duplicateMenuItemClicked),
      concatLatestFrom(() => store.select(createSelector(
        jobWorkordersFeature.selectWorkordersEditor,
        ({ workorders, actionsMenuEventId }) => workorders?.find((wo) => wo.eventId === actionsMenuEventId),
      ))),
      map(([ _action, selectedWorkorder ]) => ({
        ...selectedWorkorder,
        eventId: generateUUID(),
        charges: selectedWorkorder.charges.map((c) => ({
          ...c,
          chargeId: generateUUID(),
          new: true,
          modified: false,
        })),
        new: true,
        modified: false,
      })),
      map((workorder) => WorkOrdersActions.duplicateWorkOrderAdded({ workorder })),
    );
}, { functional: true, dispatch: true });

export const viewEventHistory = createEffect((
  $actions = inject(Actions),
  historyService = inject(HistoryService),
  store = inject(Store),
) => {
    return $actions.pipe(
      ofType(WorkOrdersActions.viewHistoryMenuItemClicked),
      concatLatestFrom(() => store.select(jobWorkordersFeature.selectWorkordersEditor)),
      tap(([ _, { actionsMenuEventId }]) => historyService.openHistory('CalendarEvent', [ actionsMenuEventId ])),
    );
  }, { functional: true, dispatch: false });

export const shareEvent = createEffect((
  $actions = inject(Actions),
  dialogService = inject(DialogService),
  store = inject(Store),
) => {
    return $actions.pipe(
      ofType(WorkOrdersActions.shareMenuItemClicked),
      concatLatestFrom(() => store.select(createSelector(
        jobWorkordersFeature.selectWorkordersEditor,
        selectJob,
        ({ actionsMenuEventId }, job) => ({
          jobId: job.id,
          preSelectedEventId: actionsMenuEventId,
          events: job.events?.filter((e) => e.status !== 'cancelled') ?? [],
        }),
      ))),
      tap(([ _, { events, jobId, preSelectedEventId }]) => dialogService.open(ShareEventZoneComponent, {
        header: 'Share Events',
        data: {
          events,
          preSelectedEventId,
          jobId,
        },
      })),
    );
  }, { functional: true, dispatch: false });

// A denied discounts.list must clear any previously loaded discounts — the
// discount picker has no discounts.list guard of its own, so stale entries
// would remain bound. The denial branch lives inside the same switchMap as the
// fetch so a denied emission cancels any in-flight request; a separate effect
// could clear the state while a late success repopulated it.
export const loadAvailableDiscounts = createEffect((
  discountsGQL = inject(DiscountsGQL),
  store = inject(Store),
  permissionHandler = inject(PermissionService),
) => {

  return store.select(
    createSelector(selectJob, (job) => job?.zone?.id),
  ).pipe(
    distinctUntilChanged(),
    filter((zoneId) => !isNil(zoneId)),
    // Pair the zone change with the first permission emission; concatLatestFrom
    // drops the value when watchPermission hasn't emitted yet, which would
    // leave discounts unloaded until the next zone change.
    concatMap((zoneId) => permissionHandler.watchPermission('discounts.list').pipe(
      take(1),
      map((hasDiscountsList) => [ zoneId, hasDiscountsList ] as const),
    )),
    map(([zoneId, hasDiscountsList]): {
      zoneId: string;
      hasDiscountsList: boolean;
      vars?: DiscountsQueryVariables;
      opts?: QueryOptionsAlone<DiscountsQueryVariables, DiscountsQuery>;
    } => {
      if (!hasDiscountsList) {
        return { zoneId, hasDiscountsList };
      }

      return {
        zoneId,
        hasDiscountsList,
        vars: {
          filter: {
            applyable: true,
            singleUse: false,
          },
          limit: -1,
        },
        opts: {
          context: {
            headers: {
              'x-zone': zoneId,
            }
          }
        },
      };
    }),
    // `switchMap`, not `exhaustMap`: a zone change arriving while the fetch is
    // active must supersede it — the active request carries the previous zone
    // in its 'x-zone' header, so letting it win would store discounts for the
    // wrong zone. The denial branch emits the clear action through this same
    // switchMap, cancelling any in-flight fetch.
    switchMap(({ hasDiscountsList, vars, opts }) => {
      if (!hasDiscountsList || !vars || !opts) {
        return of(WorkOrdersActions.availableDiscountsCleared());
      }

      return discountsGQL.fetch(vars, opts).pipe(
        map((res) => WorkOrdersActions.loadAvailableDiscountsSuccess({
          discounts: res.data?.discounts?.discounts || [],
        })),
        // End the pipe with catchError so an error doesn't terminate the effect —
        // later zone changes must still be able to reload discounts.
        catchError((error) => of(WorkOrdersActions.availableDiscountsLoadedError({ error }))),
      );
    }),
  );
}, { functional: true, dispatch: true });

export const generateChargesWithAI = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  generateChargesWithAIGQL = inject(GenerateChargesWithAiGQL),
) => {

  return actions$.pipe(
    ofType(
      WorkOrdersActions.generateChargesButtonClicked,
      WorkOrdersActions.regenerateChargesWithAI
    ),
    exhaustMap((action) => of(action).pipe(
      concatLatestFrom(() => store.select(selectJobId)),
      map(([ { eventId, type }, jobId ]): {
        vars: GenerateChargesWithAiQueryVariables;
        opts: QueryOptionsAlone<GenerateChargesWithAiQueryVariables, GenerateChargesWithAiQuery>;
        eventId: string;
      } => {

        const fetchPolicy = type === WorkOrdersActions.regenerateChargesWithAI.type ? 'network-only' : 'cache-first';

        return {
          vars: { input: { jobId, eventId }},
          opts: { fetchPolicy },
          eventId,
        }
      }),
      switchMap(({ vars, opts, eventId }) => generateChargesWithAIGQL.fetch(vars, opts).pipe(
        concatLatestFrom(() => [
          store.select(brandingFeature.selectProducts),
          store.select(brandingFeature.selectBranding),
          store.select(selectJobZone),
        ]),
        map(([ result, availableProducts, { zoneId }, jobZone ]) => {

          const {
            selectedProducts = [],
            salesAgentInstructions,
            itinerary,
            crewSize,
          } = result?.data?.generateChargesWithAI;

          const charges: EditableCharge[] = [];

          for (const selectedProduct of selectedProducts) {

            const availableProduct = availableProducts?.find((p) => p.id === selectedProduct.id);

            if (!availableProduct) { continue; }

            const price = availableProduct?.prices?.find((p) => p.id === selectedProduct.priceId);

            if (!price) { continue; }

            const amount = price.priceType === PriceType.Percentage ? price.amount : price.amount / 100;
            const taxes = (price?.taxes || [])
              .filter((t) => t.zones?.nodes?.some((z) => z?.id === zoneId || z?.id === jobZone?.id))
              .map((t) => ({ id: t.id, percentage: t.percentage }));

            charges.push({
              amount,
              quantity: selectedProduct.quantity,
              productId: selectedProduct.id,
              chargeId: generateUUID(),
              new: true,
              modified: false,
              removed: false,
              expenses: [],
              taxes,
            });
          }

          return {
            eventId,
            selectedProducts,
            agentInstructions: salesAgentInstructions,
            itinerary,
            crewSize,
            charges,
          }
        }),
        map((payload) => WorkOrdersActions.generateChargesWithAISuccess(payload)),
      )),
      catchError(error => of(WorkOrdersActions.generateChargesWithAIError({ error })))
    )),
  );
}, { functional: true, dispatch: true });

export const syncNavigatorStep = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  updateEventNavigatorGQL = inject(UpdateEventNavigatorGQL),
) => {
  return actions$.pipe(
    ofType(quickJsActions.navigatorStepConfirmed),
    concatLatestFrom(() => store.select(selectJob)),
    // Send step writes concurrently. Never drop one, never queue behind one.
    //
    // Why not `exhaustMap` (the original): it discards any confirm that arrives while a previous
    // write is still in flight — silently, with no error and therefore no rollback (#31) and no
    // toast (#35). The crew's step moves locally, the database never receives it, and the
    // divergence only surfaces later on resume. Overlap needs a write slower than the 5s undo
    // window, so it cannot happen against a local backend, but it is unremarkable on a phone.
    //
    // Why not `concatMap` (tried 2026-08-18, reverted): queueing fixed the dropped write but
    // serialised the writes, so on a throttled connection each one waited for its predecessor and
    // the round-trips stacked. Measured effect: `jobChanged` echoes arriving 16–18s after the step
    // they describe, each asserting a step the crew had long left. It did not create #46 — the
    // server was overriding the step from the originating write's own input — but it stretched the
    // window that bug needed from about two seconds to nearly twenty, which is what turned a rare
    // race into something reproducible on every run.
    //
    // `mergeMap` keeps both properties that matter: every confirm is sent, and no confirm waits on
    // another. The writes are independent — each carries its own `stepId` and timing, and the
    // server's flattener merges per step rather than replacing the map — so out-of-order completion
    // is not a correctness problem. Ordering of *arrival* was never the issue; staleness was.
    mergeMap(([ action, job ]) => {
      const { eventId, stepId, stepStartTime, previousStepId, previousStepCompletedAt, previousStepStartedAt, data } = action;

      // Merge step timing into existing navigatorData
      const event = job?.events?.find(e => e.id === eventId);
      let existingData: Record<string, any> = {};
      try { existingData = event?.navigatorData ? JSON.parse(event.navigatorData) : {}; } catch { /* ignore */ }

      const storedSteps: Record<string, any> = existingData.steps ?? {};

      // Only the two steps this advance touches. The server merges `steps` per step id (#48), so
      // every other step stays as stored — including any this device's copy has not seen yet.
      const stepsData: Record<string, any> = {};

      // Record the completion of the previous step
      if (previousStepId) {
        stepsData[previousStepId] = {
          ...(storedSteps[previousStepId] ?? {}),
          startedAt: previousStepStartedAt ?? storedSteps[previousStepId]?.startedAt ?? null,
          completedAt: previousStepCompletedAt ?? null,
        };
      }

      // Record the start of the new step (if not already recorded)
      if (stepId && !storedSteps[stepId]?.startedAt) {
        stepsData[stepId] = {
          ...(storedSteps[stepId] ?? {}),
          startedAt: stepStartTime,
          completedAt: null,
        };
      }

      // What this advance changes, and nothing else (#48). It used to carry the whole stored run
      // from this device's copy, which wrote stale keys back over newer ones. `data` is whatever the
      // confirming component supplies (nothing passes it today).
      const navigatorData = JSON.stringify({
        ...(data || {}),
        stepStartTime,
        steps: stepsData,
      });

      // Geolocation is stamped *after* the step write, not before it (#46 Stage 2).
      //
      // It used to sit in front of the mutation behind `from(geoPromise)`, so every step advance
      // waited on `getCurrentPosition` — up to the full 3s timeout on a cold fix. That delay is
      // precisely the interval in which the database still holds the previous step and a
      // whole-job echo can walk the crew backwards, so "persist immediately" is not immediate
      // while it is on the critical path. It buys nothing in exchange: nothing anywhere reads
      // `stepLocations` (#44).
      //
      // Fire-and-forget, and deliberately not awaited or error-handled beyond being swallowed —
      // a missing position must never fail a step advance. If the crew undoes before this lands
      // it will re-add an entry for a step that was not completed; `stepLocations` is explicitly
      // exempt from the undo invariant for that reason, and the append-only reshape that removes
      // the ambiguity belongs with whatever feature finally consumes it (#44).
      if (previousStepId && typeof navigator !== 'undefined' && navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          pos => store.dispatch(quickJsActions.navigatorDataPatched({
            eventId,
            // One step's entry: the server merges `stepLocations` per step id (#48).
            patch: {
              stepLocations: {
                [previousStepId]: {
                  lat: pos.coords.latitude,
                  lng: pos.coords.longitude,
                  accuracy: pos.coords.accuracy,
                  timestamp: Date.now(),
                },
              },
            },
          })),
          () => { /* no position: not an error worth surfacing to a crew mid-move */ },
          { timeout: 3000, maximumAge: 30000 },
        );
      }

      // With no signal the write waits in the navigator outbox instead (#86a), and the crew keeps
      // going: no rollback, no error toast.
      return sendOrQueueNavigatorWrite(store, updateEventNavigatorGQL, action.jobId ?? null, {
        eventId,
        currentStep: stepId,
        navigatorData,
        // Ops wins over an advance built before ops moved the crew (#86b).
        basedOnOpsRevision: opsRevisionToSend(event, undefined),
      }, {
        // Mirror the stored run into the store immediately: the server's merged result, never this
        // write's own keys, which are only part of the run (#48). Nothing else refreshes the store's
        // `navigatorData` after an advance until the websocket echo arrives
        // (`navigatorStepAdvancedLocally` patches `navigatorCurrentStep` only).
        stored: (stored) => [
          ...(action.jobId && stored != null
            ? [ WorkOrdersActions.navigatorDataUpdatedLocally({ jobId: action.jobId, eventId, navigatorData: stored }) ]
            : []),
          quickJsActions.syncNavigatorStepSuccess(),
        ],
        // Roll the optimistic advance back (#31). The store moved the step forward the moment
        // the crew confirmed; if the server refused the write, no `jobChanged` will ever arrive to
        // correct it, so without this the workorders row would claim progress that was never
        // saved and a later resume would contradict it.
        refused: (error) => [
          ...(action.jobId
              ? [ WorkOrdersActions.navigatorStepAdvancedLocally({
                  jobId: action.jobId,
                  eventId,
                  navigatorCurrentStep: action.previousPersistedStep ?? null,
              }) ]
              : []),
          quickJsActions.syncNavigatorStepError({ error }),
        ],
      });
    })
  );
}, { functional: true, dispatch: true });

/**
 * Keeps this device's copy of the run in step with a write that is waiting, so the timer and a
 * return to the navigator show the crew's own progress rather than the server's last copy.
 */
export const mirrorQueuedNavigatorSave = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
) => {
  return actions$.pipe(
    ofType(NavigatorOutboxActions.saveQueued),
    filter(({ entry }) => !!entry.jobId),
    concatLatestFrom(() => store.select(jobsFeature.selectJobs)),
    map(([ { entry }, jobs ]) => {
      const jobId = entry.jobId ?? '';
      const { eventId, currentStep } = entry.write;
      const event = jobs[jobId]?.events?.find((e) => e.id === eventId);
      const navigatorData = mergeQueuedNavigatorData(event?.navigatorData, entry.write.navigatorData);

      return currentStep === undefined
        ? WorkOrdersActions.navigatorDataUpdatedLocally({ jobId, eventId, navigatorData })
        : WorkOrdersActions.navigatorUpdatedLocally({ jobId, eventId, navigatorCurrentStep: currentStep, navigatorData });
    }),
  );
}, { functional: true, dispatch: true });

/**
 * Reverses a step advance the crew undid inside the 5-second window (#46 Stage 2).
 *
 * Since the advance is now persisted the instant Continue is tapped, undo has to un-write it.
 * The invariant it upholds: afterwards the navigator's step bookkeeping is identical to the
 * pre-confirm state. Three edits get us there:
 *
 *   1. `currentStep` back to the step being returned to.
 *   2. The returned step's `completedAt` back to `null` — it is in progress again — with its
 *      original `startedAt` restored rather than re-stamped.
 *   3. The abandoned step's entry deleted from `steps` outright. Blanking it is not enough: an
 *      entry with a `startedAt` and no `completedAt` is exactly what the page's resume path
 *      treats as "the step to come back to", so it would undo the undo on the next resume.
 *
 * `completedAt: null` is sent explicitly rather than omitted, because the backend flattener
 * needs to tell "no opinion" from "cleared" to know it must `REMOVE` the flattened
 * `navigator_<step>_completedAt` and `_actualSeconds` mirrors.
 *
 * Uses `concatMap`, not `exhaustMap`: an undo must never be dropped because another navigator
 * write happens to be in flight.
 */
export const undoNavigatorStep = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  updateEventNavigatorGQL = inject(UpdateEventNavigatorGQL),
) => {
  return actions$.pipe(
    ofType(quickJsActions.navigatorStepUndone),
    concatLatestFrom(() => store.select(selectJob)),
    concatMap(([ action, job ]) => of(null).pipe(
      switchMap(() => {
        const { stepId, eventId, jobId, abandonedStepId, restoredStepStartTime } = action;

        const event = job?.events?.find((e) => e.id === eventId);
        let existingData: Record<string, any> = {};
        try { existingData = event?.navigatorData ? JSON.parse(event.navigatorData) : {}; } catch { /* ignore */ }

        // Only the two steps the undo touches; the server merges `steps` per step id (#48).
        const stepsData: Record<string, any> = {};

        // The step being returned to: in progress again, original start time intact.
        stepsData[stepId] = {
          ...(existingData.steps?.[stepId] ?? {}),
          startedAt: restoredStepStartTime,
          completedAt: null,
        };

        // The abandoned step is sent as all-null timings, not omitted.
        //
        // This is a wire signal, not the shape that gets stored. The backend flattener only ever
        // sees the *new* JSON, so an omitted key is indistinguishable from an unmentioned one —
        // and those need opposite handling: delete `navigator_<step>_*`, or leave it alone. The
        // nulls are how we say "delete". `processNavigatorData` consumes them, emits the removals,
        // and drops the entry before storing, so the persisted JSON ends up exactly as it was
        // before the confirm — see `navigatorStepUndone`'s invariant.
        if (abandonedStepId) {
          stepsData[abandonedStepId] = { startedAt: null, completedAt: null };
        }

        const navigatorData = JSON.stringify({
          stepStartTime: restoredStepStartTime,
          steps: stepsData,
        });

        // With no signal the undo waits in the navigator outbox like any other write (#86a).
        return sendOrQueueNavigatorWrite(store, updateEventNavigatorGQL, jobId, {
          eventId,
          currentStep: stepId,
          navigatorData,
          basedOnOpsRevision: opsRevisionToSend(event, undefined),
        }, {
          stored: (stored) => [
            ...(jobId && stored != null
              ? [ WorkOrdersActions.navigatorUpdatedLocally({ jobId, eventId, navigatorCurrentStep: stepId, navigatorData: stored }) ]
              : []),
            quickJsActions.navigatorStepUndoneSuccess(),
          ],
          refused: (error) => [ quickJsActions.navigatorStepUndoneError({ error }) ],
        });
      }),
      // No optimistic rollback here. The component has already moved the crew back, and the
      // honest end state on failure is the one they asked for — re-advancing them because a
      // write failed would be the worse surprise. Surfaced as a toast instead (#35).
      catchError((error) => of(quickJsActions.navigatorStepUndoneError({ error }))),
    )),
  );
}, { functional: true, dispatch: true });

/**
 * Sends a patch to an event's `navigatorData`, leaving `navigatorCurrentStep` alone. The server
 * merges it into the stored run (#48); this effect sends the patch's keys and nothing else.
 *
 * The counterpart to `syncNavigatorStep`: same persistence, none of the step bookkeeping.
 * Components that record something about a run — rather than advance it — belong here. Using
 * `navigatorStepConfirmed` for that is what broke resume in #16.
 *
 * Also patches the in-memory job so the value is readable on the very next step without
 * waiting for the subscription round-trip; `navigatorCurrentStep` is passed through
 * unchanged, which is the whole point.
 */
export const patchNavigatorData = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  updateEventNavigatorGQL = inject(UpdateEventNavigatorGQL),
) => {
  return actions$.pipe(
    ofType(quickJsActions.navigatorDataPatched),
    concatLatestFrom(() => [ store.select(selectJobId), store.select(selectJob) ]),
    concatMap(([ { eventId, patch }, jobId, job ]) => of(null).pipe(
      switchMap(() => {
        // The patch alone. This used to spread it over the job's `navigatorData` as it stood when the
        // action was dispatched, so a patch resolving after a step advance wrote the pre-advance run
        // back over it — on every advance on a slow connection, through the location patch (#48).
        const navigatorData = JSON.stringify(patch);

        // With no signal the patch waits in the navigator outbox (#86a).
        const basedOnOpsRevision = opsRevisionToSend(job?.events?.find((e) => e.id === eventId), undefined);
        return sendOrQueueNavigatorWrite(store, updateEventNavigatorGQL, jobId ?? null, { eventId, navigatorData, basedOnOpsRevision }, {
          // Data only. This used to dispatch `navigatorUpdatedLocally`, which also carries
          // `navigatorCurrentStep` — sourced from the `selectJob` snapshot taken when the
          // action was dispatched. A patch resolving after the crew advanced therefore
          // stamped the pre-advance step back over the optimistic one and bounced them a
          // step backwards, with no server round-trip involved (#46).
          stored: (stored) => [
            ...(jobId && stored != null
              ? [ WorkOrdersActions.navigatorDataUpdatedLocally({ jobId, eventId, navigatorData: stored }) ]
              : []),
            quickJsActions.patchNavigatorDataSuccess(),
          ],
          refused: (error) => [ quickJsActions.patchNavigatorDataError({ error }) ],
        });
      }),
      catchError((error) => of(quickJsActions.patchNavigatorDataError({ error }))),
    )),
  );
}, { functional: true, dispatch: true });

export const openUpsellProductsDialogEffect = createEffect((
  actions$ = inject(Actions),
  dialogService = inject(DialogService),
) => {
  return actions$.pipe(
    ofType(WorkOrdersActions.generateChargesWithAISuccess),
    map(({eventId}) => {
      dialogService.open(UpsellProductsDialogComponent, {
        header: 'Charges generated with AI',
        data: { eventId }
      });
    }),
  );
}
, { functional: true, dispatch: false });

export const reloadWorkOrders = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
) => {
    return actions$.pipe(
      ofType(
        CreateEventDialogActions.createEventSuccess,
        CreateEventDialogActions.updateEventSuccess,
        OverviewTimelineActions.deleteEventSuccess,
        CancelAndCloseDialogActions.cancelEventsSuccess,
        CancelAndCloseDialogActions.cancelAndCloseSuccess,
        eventHelperUpdateEventSuccess,
      ),
      concatLatestFrom(() => store.select(selectJob)),
      map(([ _action, job ]) => WorkOrdersActions.workOrdersReloaded({ job })),
    );
}, { functional: true, dispatch: true });

export const replayUnsavedChanges = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
) => {
    return actions$.pipe(
      ofType(WorkOrdersActions.workOrdersReloaded),
      concatLatestFrom(() => store.select(createSelector(
        unsavedChangesFeature.selectQueue,
        selectJobId,
        (changes, jobId) => {
          if (!jobId) { return [] }
          return changes.filter((c) => c.jobId === jobId && c.source === ActionSource.WORKORDERS);
        },
      ))),
      map(([ _action, changes ]) => UnsavedChangesActions.replayTriggered({ changes })),
    );
}, { functional: true, dispatch: true });

export const openUpdateNavigatorDialog = createEffect(() => {
  const actions$ = inject(Actions);
  const dialogService = inject(DialogService);

  return actions$.pipe(
    ofType(WorkOrdersActions.updateNavigatorMenuItemClicked),
    tap(({ eventId, initialStepId }) => dialogService.open(UpdateNavigatorDialogComponent, {
      header: 'Update Navigator',
      width: '520px',
      contentStyle: { 'max-height': '80vh', 'overflow-y': 'auto' },
      data: { eventId, initialStepId },
    })),
  );
}, { functional: true, dispatch: false });

/**
 * When the crew confirms the last step, finish the run and navigate back to the workorders page.
 *
 * One mutation: `updateEventNavigator` with `currentStep: 'completed'` and the final step timing.
 * The server treats that write as the move finishing (#70): it records the real end time on the
 * event, keeps the scheduled end, and marks the event completed if it is not already. This used to
 * send its own `bulkEditCalendarEvent` for the status, which the server refused on an invoiced event
 * (a status change is a revenue-related edit) and which could not have written the end time either.
 */
export const completeNavigator = createEffect(() => {
  const actions$ = inject(Actions);
  const router = inject(Router);
  const store = inject(Store);
  const updateEventNavigatorGQL = inject(UpdateEventNavigatorGQL);

  return actions$.pipe(
    ofType(quickJsActions.navigatorCompleted),
    // Capture the current URL before navigation destroys the route context
    concatLatestFrom(() => store.select(selectJob)),
    exhaustMap(([{ eventId, lastStepId, lastStepStartedAt, lastStepCompletedAt, navigatorData }, job]) => {
      // Capture URL now, before navigation
      const currentUrl = router.url;

      // Merge last-step timing into navigatorData if not already present
      let enrichedNavData = navigatorData;
      try {
        const parsed = JSON.parse(navigatorData);
        const steps = { ...(parsed.steps ?? {}) };
        if (lastStepId && !steps[lastStepId]?.completedAt) {
          steps[lastStepId] = {
            ...(steps[lastStepId] ?? {}),
            startedAt: lastStepStartedAt,
            completedAt: lastStepCompletedAt,
          };
          enrichedNavData = JSON.stringify({ ...parsed, steps });
        }
      } catch { /* use original navigatorData */ }

      // Navigate back to workorders using the captured URL
      const leaveNavigator = () => {
        const navigatorIdx = currentUrl.split('/').indexOf('navigator');
        if (navigatorIdx >= 0) {
          const workordersUrl = currentUrl.split('/').slice(0, navigatorIdx).join('/') + '/workorders';
          router.navigateByUrl(workordersUrl);
        } else {
          router.navigate(['..'], { relativeTo: null });
        }
      };

      // Finish works with no signal too (#86a): the write waits in the navigator outbox, and the
      // real end it carries is still the crew's tap, not the moment it reaches the server.
      return sendOrQueueNavigatorWrite(store, updateEventNavigatorGQL, job?.id ?? null, {
        eventId,
        currentStep: 'completed',
        navigatorData: enrichedNavData,
        basedOnOpsRevision: opsRevisionToSend(job?.events?.find((e) => e.id === eventId), undefined),
      }, {
        // These return the helper's action lists (one action or none each), not the effect's
        // output; the rule reads any returned array literal as several actions.
        /* eslint-disable @ngrx/no-multiple-actions-in-effects */
        stored: () => {
          leaveNavigator();
          return [ quickJsActions.navigatorCompleteSuccess() ];
        },
        queued: () => {
          leaveNavigator();
          return [];
        },
        refused: (error) => {
          console.error('[completeNavigator] failed — the finish may not have been saved:', error);
          // Still navigate away even on error so crew isn't stuck
          const navigatorIdx = currentUrl.split('/').indexOf('navigator');
          if (navigatorIdx >= 0) {
            router.navigateByUrl(currentUrl.split('/').slice(0, navigatorIdx).join('/') + '/workorders');
          }
          return [ quickJsActions.navigatorCompleteError({ error }) ];
        },
        /* eslint-enable @ngrx/no-multiple-actions-in-effects */
      });
    }),
  );
}, { functional: true, dispatch: true });

// updateNavigatorConfirmed effect removed — the UpdateNavigatorDialogComponent
// now calls updateEventNavigatorGQL directly for immediate per-action saves.

/**
 * Post a job-activity comment when the crew confirms a key milestone step.
 *
 * This gives ops a real-time timeline in the job Activity tab:
 *   "Alex Ray arrived at 2271 Stevenson Rd · 3 min ago"
 *
 * The comment is attributed to the logged-in crew lead (not posted as "System")
 * so it reads naturally with the person's name.
 *
 * Always non-fatal — a failed comment never blocks step progression.
 */
const COMMENTED_STEPS = new Map<string, (ctx: { name: string; job: any; action: any }) => string | null>([
  ['drive_to_start',  ({ name, job }) => `${name} is on the way to ${startAddr(job)}`],
  ['arrival_start',   ({ name, job }) => `${name} arrived at ${startAddr(job)}`],
  // Both home-protection steps carry a required photo, and a step with a capture but no entry here
  // uploads the photo and then has nothing to attach it to — no comment is posted at all. Adding a
  // capture to a step therefore means adding it here too.
  ['home_protection_load',   ({ name, job }) => `${name}: Home protected at ${startAddr(job)}`],
  ['contract_sign',   ({ name, job }) => {
    const customer = job?.users?.find((u: any) => u.role === 'customer')?.user;
    const cName = [customer?.givenName, customer?.familyName].filter(Boolean).join(' ') || 'customer';
    return `${name}: Contract signed by ${cName}`;
  }],
  ['load_complete',   ({ name, job }) => `${name}: Load complete — departing for ${endAddr(job)}`],
  ['drive_to_end',    ({ name, job }) => `${name} is on the way to ${endAddr(job)}`],
  ['arrival_end',     ({ name, job }) => `${name} arrived at ${endAddr(job)}`],
  ['home_protection_unload', ({ name, job }) => `${name}: New home protected at ${endAddr(job)}`],
  ['final_walkthrough', ({ name })    => `${name}: Unload complete`],
  // Reads `navigatorData` for the same reason `collect_payment` does: the confirm action's `data`
  // field is declared but never populated. Registering the step is half of #63 -- without this
  // entry the component would persist the rating and no comment would ever be posted.
  ['star_rating', ({ name, job, action }) => {
    const summary = readStarRating(job, action?.eventId);
    if (!summary?.rating) { return `${name}: Reached the rating step -- no rating recorded`; }

    const stars = `${summary.rating}/5`;
    return summary.feedback
      ? `${name}: Customer rated the move ${stars}\n\n"${summary.feedback}"`
      : `${name}: Customer rated the move ${stars}`;
  }],
  // Reads `navigatorData`, not `action.data`. The confirm action declares a `data` field but nothing
  // ever populates it (`registry.ts` dispatches without one), so the original version of this line
  // always produced the bare fallback "Payment collected — payment". The component writes its summary
  // the same way the photo and signature captures write theirs.
  ['collect_payment', ({ name, job, action }) => {
    const summary = readPaymentSummary(job, action?.eventId);
    if (!summary) { return `${name}: Reached payment step — nothing recorded`; }

    const parts: string[] = [];
    if (summary.paymentRecorded) {
      const amount = formatMoney(summary.paymentAmount, summary.currency);
      const method = summary.paymentMethod ? ` (${summary.paymentMethod})` : '';
      parts.push(`payment recorded ${amount}${method}`);
    } else {
      parts.push('no payment recorded');
    }
    if (summary.invoiceSent) { parts.push('invoice sent to customer'); }

    return `${name}: ${parts.join(', ')}`;
  }],
  ['move_complete',   ({ name })      => `${name}: Move complete — returning to dock`],
]);

function startAddr(job: any): string {
  return job?.locations?.find((l: any) => l.locationType === 'start')?.location?.addressLineOne ?? 'start location';
}
function endAddr(job: any): string {
  return job?.locations?.find((l: any) => l.locationType === 'end')?.location?.addressLineOne ?? 'end location';
}

/**
 * How long the crew must stop typing before their notes are written to the database.
 * Long enough that a normal sentence is one mutation, short enough that stepping away
 * mid-thought still persists.
 */
const NAVIGATOR_NOTES_AUTOSAVE_MS = 1500;

/**
 * Turns navigator note edits into a save request once the crew stops typing.
 *
 * The route filter is what scopes this to the navigator. `summaryEditorContentChanged` is
 * shared with the job tool, which already saves through its own Save button — autosaving
 * there too would change long-standing behaviour on a page this change has no business
 * touching. See issue #14.
 *
 * A crew that types and immediately moves on is still covered: this effect lives in the
 * store, so a pending debounce survives the notes component being destroyed by the next
 * step rendering. Only killing the page outright inside the window loses the note.
 *
 * Debounced per summary key, not across the stream. A step rendering both a crew and a
 * customer notes editor would otherwise have edits to the first silently swallowed by edits
 * to the second. The live script renders one editor today, so this is insurance.
 */
export const requestNavigatorNotesSave = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
) => {
  return actions$.pipe(
    ofType(JobSummaryActions.summaryEditorContentChanged),
    concatLatestFrom(() => store.select(selectUrl)),
    filter(([ _action, url ]) => Boolean(url?.includes('/navigator/'))),
    map(([ { key } ]) => key),
    groupBy((key) => key),
    mergeMap((keyGroup$) => keyGroup$.pipe(debounceTime(NAVIGATOR_NOTES_AUTOSAVE_MS))),
    map((summaryKey) => quickJsActions.navigatorNotesSaveRequested({ summaryKey })),
  );
}, { functional: true, dispatch: true });

/**
 * Photos, the customer's signature, and damage annotations the crew recorded on a step.
 *
 * `<PhotoCapture>` and `<CustomerSignature>` both upload at capture time and store only the
 * resulting artifact id — never the image, which as base64 would add megabytes to a JSON blob
 * rewritten on every step advance (#43, and #48 for why that field is already crowded). Both write
 * through `persistStepCapture`, so this reads one shape for both.
 *
 * Tolerant by design: a missing key, a malformed blob or a step the crew photographed nothing on all
 * return empty, because a comment with no photo is fine and a step advance must never fail over one.
 */
function readStepAttachments(
  job: { events?: Array<{ id: string; navigatorData?: string | null }> } | null | undefined,
  eventId: string,
  stepId: string,
): { artifactIds: string[]; annotationText: string } {

  const empty = { artifactIds: [] as string[], annotationText: '' };

  const event = job?.events?.find((e) => e.id === eventId);
  if (!event?.navigatorData) { return empty; }

  let data: Record<string, any>;
  try { data = JSON.parse(event.navigatorData); } catch { return empty; }

  // Match on the recorded `stepId`, never on the key.
  //
  // `stepKey` is the photo component's own label and bears no relation to the step id — the script
  // uses `stepKey="load_truck_photo"` on step `load_complete`. Inferring one from the other fails on
  // exactly the two steps that have photos, so the component stores the step id explicitly.
  const entries = Object.values(data)
    .filter((value): value is Record<string, any> =>
      !!value && typeof value === 'object'
      && (value as any).stepId === stepId
      && Array.isArray((value as any).artifactIds));

  const artifactIds = entries.flatMap((v) => v.artifactIds as string[]).filter(Boolean);

  const annotations = entries
    .flatMap((v) => (v.annotations ?? []) as Array<{ reason?: string; custom?: string }>)
    .map((a) => [ a?.reason, a?.custom ].filter(Boolean).join(' — '))
    .filter(Boolean);

  const lines: string[] = [];

  if (annotations.length) {
    lines.push(`Damage noted:\n${annotations.map((a) => `• ${a}`).join('\n')}`);
  }

  // Say so in words. The signature arrives as just another image chip on the comment, so without
  // this there is nothing distinguishing it from a photo of the truck.
  if (entries.some((e) => e.kind === 'signature')) {
    lines.push('Customer signature captured.');
  }

  return { artifactIds, annotationText: lines.join('\n\n') };
}

/**
 * What the crew did on the payment step, as written by `nav-collect-payment.component.ts`.
 *
 * Same storage route and the same `stepId` discipline as `readStepAttachments` above — matched on
 * the recorded step id, never on the key.
 */
function readPaymentSummary(
  job: { events?: Array<{ id: string; navigatorData?: string | null }> } | null | undefined,
  eventId: string | undefined,
): {
  paymentRecorded?: boolean;
  paymentAmount?: number | null;
  paymentMethod?: string | null;
  invoiceSent?: boolean;
  currency?: string | null;
} | null {

  const event = job?.events?.find((e) => e.id === eventId);
  if (!event?.navigatorData) { return null; }

  let data: Record<string, any>;
  try { data = JSON.parse(event.navigatorData); } catch { return null; }

  const summary = data?.collectPayment;
  if (!summary || typeof summary !== 'object') { return null; }

  return summary;
}

/**
 * The customer's star rating, written by `NavStarRatingComponent` (#63).
 *
 * Reads **two top-level scalars** rather than one nested object, unlike `readPaymentSummary` above.
 * That is deliberate and load-bearing: `customerSatisfaction` is the key
 * `NAVIGATOR_NAMED_COLUMNS` already registers as "Customer Rating (1-5)", and the backend only
 * makes a value queryable if it is a scalar — an object gets JSON-stringified into its mirror.
 * See the note on `persistRating` for the full reasoning.
 */
function readStarRating(
  job: { events?: Array<{ id: string; navigatorData?: string | null }> } | null | undefined,
  eventId: string | undefined,
): { rating?: number; feedback?: string | null } | null {

  const event = job?.events?.find((e) => e.id === eventId);
  if (!event?.navigatorData) { return null; }

  let data: Record<string, any>;
  try { data = JSON.parse(event.navigatorData); } catch { return null; }

  const rating = data?.customerSatisfaction;
  if (typeof rating !== 'number') { return null; }

  return { rating, feedback: data?.customerSatisfactionFeedback ?? null };
}

function formatMoney(amount: number | null | undefined, currency: string | null | undefined): string {
  if (typeof amount !== 'number') { return ''; }
  try {
    return amount.toLocaleString(undefined, { style: 'currency', currency: currency || 'USD' });
  } catch {
    return `${amount.toFixed(2)}`;
  }
}

/**
 * Writes navigator notes to the job.
 *
 * Deliberately a narrow mutation rather than a dispatch into `buildSaveChangesVariables`,
 * even though that effect already knows how to save summaries. Two reasons:
 *
 * 1. It saves **everything** pending on the job — locations, charges, workorders — so a crew
 *    finishing a sentence would flush whatever an office user left dirty earlier in the
 *    session. Not a decision a tablet in a driveway should be making.
 * 2. It rewrites `calculatorOutput__*` metadata on every run, unconditionally
 *    (`job.effects.ts`, "Update outputs regardless"). Those values are read straight from
 *    the persisted store, so they survive onto the navigator route. Writing pricing metadata
 *    as a side effect of typing a note is exactly the class of bug that has already cost
 *    this project real time.
 *
 * One field, one mutation, no side effects.
 */
export const saveNavigatorNotes = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  updateJobGQL = inject(UpdateJobGQL),
) => {
  return actions$.pipe(
    ofType(quickJsActions.navigatorNotesSaveRequested),
    concatMap((action) => of(action).pipe(
      concatLatestFrom(() => [
        store.select(selectJobId),
        store.select(jobCreateSummaryFeature.selectSummaryEditors),
        store.select(selectSaveableJobPageChanges),
      ]),
      exhaustMap(([ { summaryKey }, jobId, summaryEditors, changes ]) => {

        if (!jobId) {
          throw new Error('Cannot save navigator notes: no job in the current route');
        }

        // Clear only the summary changes. Anything else pending on the job is not ours to
        // resolve, and must stay queued for whoever does save it.
        const changeIds = changes
          .filter((c) => c.source === ActionSource.SUMMARY_OVERVIEW)
          .map((c) => c.id);

        return updateJobGQL.mutate({
          updateJobs: {
            jobId,
            [summaryKey]: { contents: JSON.stringify(summaryEditors[summaryKey]) },
          },
        }).pipe(
          tap((res) => {
            if (!res.data?.updateJobs?.isSuccess) {
              throw new Error('Failed to save navigator notes');
            }
          }),
          map(() => quickJsActions.saveNavigatorNotesSuccess({ changeIds })),
        );
      }),
      catchError((error) => of(quickJsActions.saveNavigatorNotesError({ error }))),
    )),
  );
}, { functional: true, dispatch: true });

/**
 * Requirement: `#6951` - saves the Crew Pay assignment boxes of a job shared with the current
 * zone, through setCrewPayAssignments.
 *
 * Listens to the same Save buttons as `buildSaveChangesVariables`, which skips these changes on a
 * shared job (`selectJobSaveChanges`), so each change is saved by exactly one of the two. Like
 * `saveNavigatorNotes`, it claims only its own changes. No zone override: the request runs in the
 * current zone, which is where the server checks the share and jobCrewPay.editShared.
 *
 * The calculator output is sent as the browser computed it. Its figures do not depend on the
 * zone running the calculator, and the full job save writes them the same way.
 */
export const saveSharedCrewPay = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  setCrewPayAssignmentsGQL = inject(SetCrewPayAssignmentsGQL),
) => {
  return actions$.pipe(
    ofType(
      JobToolActions.saveChangesButtonClicked,
      JobHeaderActions.saveButtonClicked,
      JobHeaderActions.saveJobFooterClicked,
      LeaveConfirmationDialogActions.saveChangesButtonClicked,
      WorkOrdersActions.saveButtonClicked,
      UnsavedChangesBannerActions.saveChangesButtonClicked,
    ),
    exhaustMap((action) => of(action).pipe(
      concatLatestFrom(() => [
        store.select(selectJobId),
        store.select(selectSharedCrewPayChanges),
        store.select(selectUserInputs),
        store.select(selectCalculatedState),
      ]),
      filter(([ , jobId, changes ]) => Boolean(jobId) && changes.length > 0),
      mergeMap(([ , jobId, changes, userInputs, calculatedState ]) => {
        const changeIds = changes.map((c) => c.id);

        // Several toggles of one box are several changes; the box's current value is what saves.
        const inputs: Record<string, boolean> = {};
        for (const change of changes) {
          const key = crewPayAssignmentChangeKey(change)!;
          inputs[key] = userInputs[key] === true || userInputs[key] === 'true';
        }

        const assignments: CrewPayAssignmentInput[] = Object.entries(inputs).map(([ key, enabled ]) => ({
          ...parseCrewPayAssignmentKey(key)!,
          enabled,
        }));

        // Only primitives are stored, matching the full job save.
        const calculatorState = Object.fromEntries(Object.entries(calculatedState ?? {})
          .filter(([ , value ]) => [ 'string', 'number', 'boolean' ].includes(typeof value)));

        return concat(
          of(quickJsActions.saveSharedCrewPayStarted({ changeIds })),
          setCrewPayAssignmentsGQL.mutate({ jobId: jobId!, assignments, calculatorState }).pipe(
            tap((res) => {
              if (!res.data?.setCrewPayAssignments) {
                throw new Error('Failed to save crew pay');
              }
            }),
            map(() => quickJsActions.saveSharedCrewPaySuccess({ changeIds, inputs })),
            // Here rather than only below, so the error carries the ids marked as saving.
            catchError((error) => of(quickJsActions.saveSharedCrewPayError({ error, changeIds }))),
          ),
        );
      }),
      catchError((error) => of(quickJsActions.saveSharedCrewPayError({ error, changeIds: [] }))),
    )),
  );
}, { functional: true, dispatch: true });

export const postNavigatorStepComment = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  createCommentGQL = inject(CreateCommentGQL),
) => {
  return actions$.pipe(
    ofType(quickJsActions.navigatorStepConfirmed),
    filter(({ previousStepId }) => !!previousStepId && COMMENTED_STEPS.has(previousStepId!)),
    concatLatestFrom(() => store.select(selectJob)),
    exhaustMap(([action, job]) => {
      const stepId = action.previousStepId!;
      const textFn = COMMENTED_STEPS.get(stepId);
      if (!textFn || !job) return of(null);

      // `selectSignal` rather than subscribe-then-unsubscribe: the old form hid a store read in an
      // imperative side channel and would silently yield the default if the selector ever became
      // async. Matches `update-navigator-dialog.component.ts`.
      const user = store.selectSignal(authFeature.selectUser)();
      const crewLeadName = [user?.givenName, user?.familyName].filter(Boolean).join(' ') || 'Crew lead';

      const text = textFn({ name: crewLeadName, job, action });
      if (!text) return of(null);

      const jobId = job?.id;
      if (!jobId || typeof jobId !== 'string') {
        console.warn('[postNavigatorStepComment] job.id missing or invalid, skipping comment');
        return of(null);
      }

      // Attach anything the crew photographed on this step, and append what they annotated.
      //
      // The photos were uploaded at capture time and only their artifact ids live in
      // `navigatorData` (see `navigatorDataPatched` from the photo component), so this is a lookup,
      // not an upload — the step advance is not delayed by the network. `addArtifacts` is the same
      // field manual job comments use, so the photos land on the job's activity feed exactly like a
      // human attachment.
      const { artifactIds, annotationText } = readStepAttachments(job, action.eventId, stepId);

      const commentText = annotationText ? `${text}\n\n${annotationText}` : text;

      console.log('[postNavigatorStepComment]', {
        stepId, jobId, objectType: 'Job', photos: artifactIds.length,
      });

      return createCommentGQL.mutate({
        input: {
          ...(artifactIds.length ? { addArtifacts: artifactIds } : {}),
          objectId: jobId,
          objectType: 'Job',
          text: commentText,
          mode: 'System',
          attributes: ['navigator', stepId],
        },
      }).pipe(
        catchError(err => {
          // Non-fatal — a failed comment must never surface an error to the crew
          console.warn('[postNavigatorStepComment] non-fatal:', err);
          return of(null);
        }),
      );
    }),
  );
}, { functional: true, dispatch: false });
